import React, { useState, useRef, useEffect } from 'react';
import { Marked } from '../common/Marked';
import Row from '../common/Row';
import Breadcrumbs from '../common/Breadcrumbs';
import TimeStamp from '../common/TimeStamp';
import FieldsetIndex from '../fieldsets/FieldsetIndex';
import FilterFields from '../filter-fields/FilterFields';
import FormEdit from './FormEdit';
import Permalink from '../common/Permalink';
import Icon from '../common/Icon';
import { UserContext, FormContext } from '../../hooks/Contexts';
import { userCan } from '../../helpers/rolePermissions';
import { Link, useLoaderData } from 'react-router-dom';
import { adjustSortablesAfterDelete, sortable } from '../../helpers/sorters';
import FormMetaEntries from './form-meta-entries/FormMetaEntries';
import CustomFilterFields from '../filter-fields/CustomFilterFields';

const FormShowEdit = () => {
  const { data } = useLoaderData();
  const { current_user, permission, custom_filters } = data;
  const { filter_fields, container, form_meta_entries } = data.form;
  const [form, setForm] = useState(data.form);
  const [editing, setEditing] = useState(false);
  const [showMeta, setShowMeta] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [formMetaEntries, setFormMetaEntries] = useState(form_meta_entries);
  const [filterFields, setFilterFields] = useState(sortable(filter_fields));
  const [userFilterFields, setUserFilterFields] = useState(
    sortable(custom_filters)
  );
  const [fieldsets, setFieldsets] = useState(sortable(data.form.fieldsets));

  const user = {
    id: current_user.id,
    type: Object.keys(current_user).includes('internal_link')
      ? 'external_user'
      : 'user',
    ...userCan(current_user, permission),
  };

  const informantField = () => {
    if (fieldsets.length) {
      let fields = fieldsets.map((fs) => fs.fields).flat();
      let informant = fields.find((field) => field?.informs_global);
      return informant || false;
    }
    return false;
  };

  const toggleEdit = () => {
    setEditing(!editing);
  };

  // if fieldset is deleted, remove filter fields (if any)
  const fieldsetDeleted = (fieldset) => {
    let fieldIds = fieldset.fields?.map((f) => f.id) || [];
    let filterableFieldIds = filterFields.map((ff) => ff.field_id);
    let deletedFieldsFilterable = filterableFieldIds.filter((id) =>
      fieldIds.includes(id)
    );
    if (deletedFieldsFilterable.length > 0) {
      setFilterFields((ffs) =>
        ffs.filter((ff) => !deletedFieldsFilterable.includes(ff.field_id))
      );
    }
    setFieldsets(adjustSortablesAfterDelete(fieldsets, fieldset.id));
  };

  const {
    active,
    archived,
    completion_text,
    course_id,
    course_validate,
    created_at,
    description,
    display_name,
    global_quota,
    inactive_text,
    is_parent,
    linked_parent_form_id,
    name,
    password,
    single_use_only,
    slug,
    splash,
    submission_validate,
    updated_at,
  } = form;

  const detailsRef = useRef();
  const detailsSectionHeight = detailsRef?.current?.scrollHeight || '1000';
  const detailsStyle = {
    maxHeight: showDetails ? `${detailsSectionHeight}px` : '0px',
    transition: 'max-height 0.5s ease-in-out',
    overflow: 'hidden',
  };

  useEffect(() => {
    setShowMeta(editing);
  }, [editing]);

  // Form info that is collapsible & not typically needed for immediate viewing
  const FormDetails = () => (
    <>
      <Row
        name='Splash Page Text: (Optional)'
        value={<Marked text={splash} />}
      />
      <Row
        name='Inactive Text: (Optional)'
        value={<Marked text={inactive_text} />}
      />
      <Row name='Description:' value={<Marked text={description} />} />
      <Row name='Completion Text:' value={<Marked text={completion_text} />} />
      <Row
        name='Global Quota:'
        value={global_quota ? global_quota : 'Not Set'}
      />
      <Row name='Active:' value={active ? 'Yes' : 'No'} />
      <Row name='Archived:' value={archived ? 'Yes' : 'No'} />
      <Row name='Is Parent:' value={is_parent ? 'Yes' : 'No'} />
      <Row name='Is Child:' value={submission_validate ? 'Yes' : 'No'} />
      {submission_validate && (
        <>
          <Row name='Single Use:' value={single_use_only ? 'Yes' : 'No'} />
          <Row
            name='Parent Form ID:'
            value={
              <Link to={`/manage/forms/${linked_parent_form_id}`}>
                {linked_parent_form_id}
              </Link>
            }
          />
        </>
      )}
      <Row name='Course Validated:' value={course_validate ? 'Yes' : 'No'} />
      {course_validate && <Row name='SSLP Course ID:' value={course_id} />}
      {(submission_validate || course_validate) && (
        <Row name='Form Password:' value={password} />
      )}
      <TimeStamp title='Created' value={created_at} />
      <TimeStamp title='Updated' value={updated_at} />
    </>
  );

  return (
    <UserContext.Provider value={user}>
      <FormContext.Provider
        value={{
          form,
          setForm,
          formMetaEntries,
          setFormMetaEntries,
          fieldsets,
        }}
      >
        <Breadcrumbs
          form={{ name: name, id: form.id }}
          container={{ name: container.name, id: container.id }}
        />
        {editing ? (
          <FormEdit toggleEdit={toggleEdit} informant={informantField()} />
        ) : (
          <div className='details'>
            {user.isAdmin && (
              <div className='controls'>
                <Icon
                  url={`/manage/forms/${form.id}/mailers`}
                  icon='envelope'
                  title={'Edit form mailers'}
                  className='edit'
                  isLink
                />
                <Icon className='edit' onClick={toggleEdit} icon='pencil' />
              </div>
            )}
            <Row name='Display Name:' value={display_name} />
            <Row name='Name:' value={name} />
            <Permalink containerSlug={container.slug} formSlug={form.slug} />

            {/* Collapsible area starts here */}
            <div className='row'>
              <hr />
              <div className='name'>
                <h3>Form Details</h3>
              </div>
              <div className='value form-meta-icons'>
                <Icon
                  title={showDetails ? 'Hide Details' : 'Show Details'}
                  className={'right'}
                  icon={`chevron-${showDetails ? 'up' : 'down'}`}
                  onClick={() => setShowDetails(!showDetails)}
                />
              </div>
            </div>
            <div id='form-details' style={detailsStyle} ref={detailsRef}>
              <FormDetails />
            </div>
            {/* Collapsible area ends */}

            {formMetaEntries.length > 0 && (
              <FormMetaEntries
                show={showMeta}
                toggle={() => setShowMeta(!showMeta)}
              />
            )}
          </div>
        )}

        {/* Edit Default Filter Fields (Manage/Admins) */}
        <FilterFields
          filterFields={filterFields}
          setFilterFields={setFilterFields}
          userCanEdit={user.isAdmin}
        />

        {/* Edit User Custom Filter Fields (all users) */}
        <CustomFilterFields
          userFilterFields={userFilterFields}
          setUserFilterFields={setUserFilterFields}
        />

        <div className='right'>
          <Link to={`/manage/forms/${form.id}`}>
            <button className='save-return' disabled={editing}>
              Save & Return
            </button>
          </Link>
        </div>

        {user.isAdmin && (
          <FieldsetIndex
            setFieldsets={setFieldsets}
            formIsChild={submission_validate}
            fieldsetDeleted={fieldsetDeleted}
          />
        )}
      </FormContext.Provider>
    </UserContext.Provider>
  );
};

export default FormShowEdit;
